import {
  announcementService,
  getCommandBotServices,
  getProductsAggregate,
  PRODUCT_ACCESS_MODES,
  PRODUCT_TYPES
} from '@moal/api'
import { KevelProvider } from 'adcontrolx-lib'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { PRODUCT_MAP } from '../constants/products'
import * as SETTINGS from '../constants/settings'
import useIsPortraitReady from '../hooks/useIsPortraitReady'
import { getActivePoll } from '../utils/APIs/polls'
import { getAllSettings, postSetting, putSetting } from '../utils/APIs/settings'
import { PRODUCT_CODE } from '../utils/ENV'

function buildSettingsMap(fetchedSettings = []) {
  const settingsMap = {}

  const isObjectButNotArray = (setting) => {
    return (
      setting &&
      setting.value instanceof Object &&
      !Array.isArray(setting.value)
    )
  }

  SETTINGS.ALL_SETTINGS.forEach(({ key, default: defaultValue }) => {
    // we are iterating over each setting defined. For each one
    // we try to find it in the returned array of settings. If we do
    // we use the found value, otherwise we return the defined default
    const setting = fetchedSettings.find(({ setting }) => setting === key)
    settingsMap[key] = setting
      ? isObjectButNotArray(setting)
        ? { ...defaultValue, ...setting.value } // need to spread the saved object over the default one
        : setting.value
      : defaultValue
  })

  return settingsMap
}

// We define a default value for the context on creation
// We will want to have status for isLoading, isError, and settings
// By default we will assume isLoading to be true and isError and settings to be false/null
export const SiteConfigContext = React.createContext({
  isLoading: true,
  isError: false,
  settings: [],
  settingsMap: {},
  kevelIds: null,
  updateSetting: async () => {}
})

export const SiteConfigProvider = ({ children }) => {
  const [productsService, setProductService] = useState()
  const [state, setState] = useState({
    isLoading: true,
    isError: false,
    settings: [],
    settingsMap: buildSettingsMap(),
    kevelIds: null
  })
  const isPortraitReady = useIsPortraitReady()

  const [latestAnnouncement, setLatestAnnouncement] = useState()
  const [activePoll, setActivePoll] = useState()
  const [botMessages, setBotMessages] = useState()
  const [replyTo, setReplyTo] = useState()

  useEffect(() => {
    loadSettings()
    loadBotMessages()
    loadActivePoll()
    loadLatestAnnouncement()
  }, [])

  const loadProduct = async () => {
    const response = await getProductsAggregate({
      token: localStorage.getItem('authToken')
    })

    if (response && response.status === 200) {
      const bundles = response.data['bundles'] || []
      const hub = response.data['hub'] || null
      const org = response.data['org'] || null
      const products = response.data['products'] || []

      const data = {}
      data[PRODUCT_TYPES.ORG] = org
      data[PRODUCT_TYPES.HUB] = hub
      data[PRODUCT_TYPES.BUNDLE] = bundles
      data[PRODUCT_MAP.THIS_PRODUCT] = products.find(
        (each) => each.itemNumber === PRODUCT_CODE
      )
      data[PRODUCT_MAP.OTHER_PRODUCTS] = products.filter(
        (each) => each.itemNumber !== PRODUCT_CODE
      )

      setState((prevState) => ({
        ...prevState,
        kevelIds: {
          ...(data[PRODUCT_MAP.THIS_PRODUCT]?.kevel || {}),
          networkId: data[PRODUCT_TYPES.ORG].kevelNetworkId || null
        }
      }))
      setProductService(data)
    }
  }

  const loadSettings = async () => {
    // Set our state to isLoading
    setState((prevState) => ({ ...prevState, isLoading: true }))
    // get our settings
    const response = await getAllSettings()
    if (response && response.status === 200) {
      // update our state with settings
      // we will keep the array of settings as is
      // but also create a key value map of all settings
      const settings = [...response.data]
      const settingsMap = buildSettingsMap(settings)
      await loadProduct()
      setState((prevState) => ({
        ...prevState,
        isLoading: false,
        isError: false,
        settings,
        settingsMap
      }))
    } else {
      setState((prevState) => ({
        ...prevState,
        isLoading: false,
        isError: true,
        settings: [],
        settingsMap: {}
      }))
    }
  }

  // This function will take a setting const and the new value
  // and make the update API call.
  const updateSetting = async ({ key }, value) => {
    // First we will find the setting in the settings array
    const settingsArray = [...state.settings]
    const settingIndex = settingsArray.findIndex(
      ({ setting }) => setting === key
    )
    let response
    if (settingIndex !== -1) {
      // we found the setting, so let's make the update call
      const settingId = settingsArray[settingIndex]._id
      response = await putSetting(settingId, value)
      if (response && response.status === 200) {
        // we successfully updated the setting
        // update the setting in the array
        settingsArray[settingIndex] = response.data
      }
    } else {
      // no such setting was found, let's create it
      response = await postSetting(key, value)
      if (response && response.status === 201) {
        // we successfully created the setting
        // push the new setting into our array
        settingsArray.push(response.data)
      }
    }
    // After either updating or creating the setting, we want
    // to update our state
    const updatedSettingsMap = buildSettingsMap(settingsArray)
    setState((prev) => ({
      ...prev,
      settings: settingsArray,
      settingsMap: updatedSettingsMap
    }))

    return response
  }

  const setSettings = (data) => {
    setState((prevState) => ({
      ...prevState,
      settings: data,
      settingsMap: buildSettingsMap(data)
    }))
  }

  const loadBotMessages = async () => {
    const response = await getCommandBotServices({
      itemNumber: PRODUCT_CODE
    })
    if (response && response.status === 200) {
      setBotMessages(response.data)
    }
  }

  const updateActivePoll = (data) => {
    switch (data.status) {
      case 'LIVE':
        setActivePoll(data.poll)
        break
      case 'POST':
      case 'VOTE':
      case 'CLOSE':
        if (activePoll && data.poll._id === activePoll._id) {
          setActivePoll(data.poll)
        }
        break
      case 'ARCHIVE':
      case 'DELETE':
        if (activePoll && data.poll._id === activePoll._id) {
          setActivePoll(null)
        }
        break
    }
  }

  const loadActivePoll = async () => {
    const response = await getActivePoll()
    if (response && response.status === 200) {
      setActivePoll(response.data)
    }
  }

  const loadLatestAnnouncement = async () => {
    try {
      const response = await announcementService.getLatest(PRODUCT_CODE)
      setLatestAnnouncement(response.data)
    } catch {
      // Do nothing
    }
  }

  const getActiveStreamer = () => {
    if (state.settingsMap[SETTINGS.STREAMERS.key]) {
      const found = state.settingsMap[SETTINGS.STREAMERS.key].find(
        (streamer) => streamer.active === true
      )
      if (found) {
        return found
      }
    }
    return null
  }

  const freeEnabled = () => {
    if (productsService) {
      return (
        productsService?.[PRODUCT_MAP.THIS_PRODUCT]?.access?.mode ===
        PRODUCT_ACCESS_MODES.FREE
      )
    }
    return false
  }

  return (
    <SiteConfigContext.Provider
      value={{
        isLoading: state.isLoading,
        isError: state.isError,
        productsService,
        freeEnabled: freeEnabled(),
        updateSetting,
        setSettings,
        activePoll,
        updateActivePoll,
        latestAnnouncement,
        setLatestAnnouncement,
        loadLatestAnnouncement,
        botMessages,
        replyTo,
        setReplyTo,
        activeStreamer: getActiveStreamer(),
        kevelIds: state.kevelIds,
        ...state.settingsMap
      }}
    >
      {state.kevelIds && state.kevelIds.siteId && state.kevelIds.networkId ? (
        <KevelProvider
          portraitReady={isPortraitReady}
          networkId={state.kevelIds.networkId}
          siteId={state.kevelIds.siteId}
        >
          {children}
        </KevelProvider>
      ) : (
        children
      )}
    </SiteConfigContext.Provider>
  )
}

SiteConfigProvider.propTypes = {
  children: PropTypes.any
}
